/**
 * PublicAvailability — public-facing component on PropertyLanding.
 * Shows either:
 *  - A small calendar (2 months) with red X on blocked dates, green dot on free dates
 *  - OR an embedded channel-manager HTML widget if host opted-in
 *
 * Clicking a free date opens a popup with: phone, email, OTA links (whatever the host configured).
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, Phone, Mail, X, ChevronLeft, ChevronRight, Globe, MessageCircle, Moon } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import { computePriceForDate, formatPrice, resolveExtraFee } from "../utils/pricing";

const monthNames = {
  it: ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
  fr: ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"],
  de: ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"],
  es: ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"],
};
const weekDays = ["L","M","M","G","V","S","D"];

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const prettyDate = (iso, lang) => {
  const [y, m, d] = iso.split("-");
  const months = monthNames[lang] || monthNames.it;
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicAvailability = ({ property, theme, inline = false }) => {
  const { t, lang } = useLanguage();
  const mode = property?.availability_mode || "calendar";
  const pricing = property?.pricing;
  const pricingActive = !!(pricing?.enabled && pricing?.base_price > 0);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorDate, setAnchorDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [popupDate, setPopupDate] = useState(null);

  // Fetch public availability blocks
  useEffect(() => {
    if (mode !== "calendar" || !property?.property_id) { setLoading(false); return; }
    axios.get(`${API}/properties/${property.property_id}/availability-public`)
      .then(r => setBlocks(r.data.blocks || []))
      .catch(() => setBlocks([]))
      .finally(() => setLoading(false));
  }, [mode, property?.property_id]);

  const blockedSet = useMemo(() => {
    const s = new Set();
    for (const b of blocks) { if (b.blocked) s.add(b.date); }
    return s;
  }, [blocks]);

  // ─── Channel widget mode ───
  if (mode === "channel_widget" && property?.channel_widget_html) {
    return (
      <section className={inline ? "" : "py-10 md:py-16 px-4 md:px-8 max-w-6xl mx-auto"} data-testid="public-availability">
        <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-1" style={{ color: theme?.text || "#18181b" }}>{t("pl.availability.bookable")}</h2>
        <p className="text-sm mb-6" style={{ color: theme?.sub || "#71717A" }}>{t("pl.availability.check_book")}</p>
        <div className={inline ? "rounded-2xl overflow-hidden" : "rounded-3xl overflow-hidden shadow-md border"} style={{ borderColor: theme?.border || "#e4e4e7" }}
          dangerouslySetInnerHTML={{ __html: property.channel_widget_html }} />
      </section>
    );
  }

  if (mode !== "calendar") return null;

  // ─── Calendar mode ───
  const renderMonth = (offset) => {
    const base = new Date(anchorDate);
    base.setMonth(base.getMonth() + offset);
    const year = base.getFullYear();
    const month = base.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startOffset = (firstDay.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

    const today = new Date(); today.setHours(0, 0, 0, 0);

    return (
      <div className="flex-1 min-w-[260px]">
        <p className="text-center font-bold text-sm mb-3 capitalize" style={{ color: theme?.text || "#18181b" }}>{(monthNames[lang] || monthNames.it)[month]} {year}</p>
        <div className="grid grid-cols-7 gap-1 text-[10px] font-bold mb-1" style={{ color: theme?.sub || "#a1a1aa" }}>
          {weekDays.map((d, i) => <div key={i} className="text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={idx} className="aspect-square" />;
            const ds = fmtDate(cell);
            const isPast = cell < today;
            const isBlocked = blockedSet.has(ds);
            const isFree = !isPast && !isBlocked;
            const priceRes = isFree && pricingActive ? computePriceForDate(pricing, ds) : null;
            const isPromo = !!priceRes?.promo;
            return (
              <button
                key={idx}
                type="button"
                disabled={isPast || isBlocked}
                onClick={() => { if (isFree) setPopupDate(ds); }}
                className={`aspect-square rounded-lg text-xs font-bold transition-all relative flex flex-col items-center justify-center ${
                  isFree ? "hover:scale-110 hover:shadow-md cursor-pointer" : "cursor-not-allowed"
                } ${isPromo ? "animate-pulse ring-2 ring-amber-400" : ""}`}
                style={{
                  background: isBlocked ? "#DC2626" : isPromo ? "#FEF3C7" : isFree ? "#ECFDF5" : "transparent",
                  color: isBlocked ? "#fff" : isPast ? "#d4d4d8" : isPromo ? "#92400E" : isFree ? "#065F46" : (theme?.text || "#18181b"),
                  border: isPromo ? "1.5px solid #F59E0B" : isFree ? "1.5px solid #10B981" : "none",
                }}
                data-testid={`avail-day-${ds}`}
                title={isBlocked ? "Occupato" : isPromo ? `Promo −${priceRes.discount_pct}%${priceRes.note ? " · " + priceRes.note : ""}` : isFree ? "Disponibile — clicca per contattare l'host" : ""}
              >
                <span className={priceRes ? "text-[11px] leading-none" : ""}>{cell.getDate()}</span>
                {priceRes && (
                  <span className={`mt-0.5 text-[9px] font-black leading-none ${isPromo ? "text-amber-700" : "text-emerald-700"}`}>
                    {formatPrice(priceRes.price)}
                  </span>
                )}
                {isBlocked && <X className="absolute inset-0 m-auto w-3 h-3 opacity-60 pointer-events-none" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Build contact channels for popup
  const phone = property?.phone || "";
  const email = property?.email || property?.contact_email || "";
  const whatsapp = property?.whatsapp || "";
  const otaLinks = property?.ota_links || {};

  return (
    <>
      <section className={inline ? "" : "py-10 md:py-16 px-4 md:px-8 max-w-6xl mx-auto"} data-testid="public-availability">
        <div className="mb-3">
          <h2 className={inline ? "text-lg md:text-xl font-black tracking-tight" : "text-2xl md:text-3xl font-black tracking-tight"} style={{ color: theme?.text || "#18181b" }}>{t("pl.availability.title")}</h2>
          <p className={inline ? "text-xs" : "text-sm"} style={{ color: theme?.sub || "#71717A" }}>{t("pl.availability.subtitle")}</p>
        </div>

        <div className={inline ? "" : "bg-white rounded-3xl shadow-sm border p-4 md:p-6"} style={inline ? {} : { borderColor: theme?.border || "#e4e4e7" }}>
          {/* Disclaimer prezzi (solo se pricing attivo) */}
          {pricingActive && !loading && (() => {
            const priceStr = formatPrice(pricing.base_price);
            const guestsN = Number(pricing.base_guests || 0);
            const guestsStr = guestsN === 1
              ? t("pl.pricing.persons_one")
              : t("pl.pricing.persons_many").replace("{n}", guestsN);
            const taxDecimals = pricing.tourist_tax_per_person % 1 ? 1 : 0;
            const minNights = Number(pricing.min_nights || 0);
            const minNightsLabel = minNights > 0
              ? (minNights === 1
                  ? t("pl.pricing.min_nights_one")
                  : t("pl.pricing.min_nights_many").replace("{n}", minNights))
              : null;
            return (
            <div className="mb-3 md:mb-4 space-y-2">
              {/* KPI prezzo — hero evidenziato */}
              <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/50 px-3.5 py-2.5 md:px-4 md:py-3" data-testid="pl-pricing-disclaimer">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex flex-col leading-none">
                    <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.15em] text-amber-700 mb-1">{t("pl.pricing.starting_from")}</span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl md:text-3xl font-black tracking-tight text-amber-900">{priceStr}</span>
                      {guestsN > 0 && <span className="text-[11px] md:text-xs font-bold text-amber-700/80">/ {guestsStr}</span>}
                    </div>
                  </div>
                  {minNightsLabel && (
                    <span
                      className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-amber-800"
                      data-testid="pl-min-nights"
                    >
                      <Moon className="w-3 h-3" />
                      {minNightsLabel}
                    </span>
                  )}
                </div>
                <p className="text-[10px] md:text-[11px] leading-snug text-amber-800/70 mt-1.5">{t("pl.pricing.note_extra")}</p>
              </div>

              {/* Costi aggiuntivi: chip compatti */}
              {(() => {
                const validExtras = (Array.isArray(pricing.extra_fees) ? pricing.extra_fees : [])
                  .filter((f) => f && Number(f.amount) > 0);
                return (pricing.cleaning_fee > 0 || pricing.tourist_tax_per_person > 0 || validExtras.length > 0) && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 px-3.5 py-2.5 md:px-4 md:py-3" data-testid="pl-extra-costs">
                  <p className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-1.5">{t("pl.pricing.extra_costs")}</p>
                  <div className="flex flex-wrap gap-2">
                    {pricing.cleaning_fee > 0 && (
                      <div className="inline-flex items-center gap-2 bg-white/70 rounded-lg px-2.5 py-1.5">
                        <span className="text-[11px] md:text-[12px] text-emerald-900 font-medium">🧹 {t("pl.pricing.cleaning_fee")}</span>
                        <span className="text-[13px] md:text-sm font-black text-emerald-700">{formatPrice(pricing.cleaning_fee)}</span>
                      </div>
                    )}
                    {pricing.tourist_tax_per_person > 0 && (
                      <div className="inline-flex items-center gap-2 bg-white/70 rounded-lg px-2.5 py-1.5">
                        <span className="text-[11px] md:text-[12px] text-emerald-900 font-medium">🏛️ {t("pl.pricing.tourist_tax")}</span>
                        <span className="text-[13px] md:text-sm font-black text-emerald-700">
                          €{Number(pricing.tourist_tax_per_person).toLocaleString("it-IT", { minimumFractionDigits: taxDecimals })}
                          <span className="text-[10px] font-medium text-emerald-600 ml-1">{t("pl.pricing.per_person_night")}</span>
                        </span>
                      </div>
                    )}
                    {validExtras.map((f) => {
                      const { emoji, label } = resolveExtraFee(f, lang);
                      const unitTxt = { it: f.unit === "night" ? "/notte" : "/soggiorno", en: f.unit === "night" ? "/night" : "/stay", fr: f.unit === "night" ? "/nuit" : "/séjour", es: f.unit === "night" ? "/noche" : "/estancia", de: f.unit === "night" ? "/Nacht" : "/Aufenthalt" };
                      return (
                        <div key={f.id || label} className="inline-flex items-center gap-2 bg-white/70 rounded-lg px-2.5 py-1.5" data-testid={`pl-extra-fee-${f.id || label}`}>
                          <span className="text-[11px] md:text-[12px] text-emerald-900 font-medium">{emoji} {label}</span>
                          <span className="text-[13px] md:text-sm font-black text-emerald-700">
                            {formatPrice(f.amount)}
                            <span className="text-[10px] font-medium text-emerald-600 ml-1">{unitTxt[lang] || unitTxt.it}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}
            </div>
            );
          })()}
          {loading ? (
            <div className="text-center text-sm text-zinc-400 py-8">{t("pl.availability.loading")}</div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row gap-6">
                {renderMonth(0)}
                {renderMonth(1)}
              </div>
              {/* Navigazione mesi — minimal, sotto il calendario */}
              <div className="mt-4 pt-3 flex items-center justify-between gap-3 border-t" style={{ borderColor: theme?.border || "#f1f1f3" }}>
                <button
                  type="button"
                  onClick={() => { const d = new Date(anchorDate); d.setMonth(d.getMonth() - 1); setAnchorDate(d); }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] md:text-sm font-bold text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  data-testid="public-cal-prev"
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={2.4} />
                  {t("pl.availability.prev_month")}
                </button>
                <button
                  type="button"
                  onClick={() => { const d = new Date(anchorDate); d.setMonth(d.getMonth() + 1); setAnchorDate(d); }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] md:text-sm font-bold text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  data-testid="public-cal-next"
                >
                  {t("pl.availability.next_month")}
                  <ChevronRight className="w-4 h-4" strokeWidth={2.4} />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Popup contatti su click data libera */}
      <AnimatePresence>
        {popupDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[4000] flex items-end md:items-center justify-center p-0 md:p-6"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
            onClick={() => setPopupDate(null)}
            data-testid="avail-popup"
          >
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.96 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-6 md:p-7 shadow-2xl"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-1">{t("pl.availability.popup_label")}</p>
                  <h3 className="text-xl font-black tracking-tight text-zinc-900">{prettyDate(popupDate, lang)}</h3>
                </div>
                <button onClick={() => setPopupDate(null)} className="w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center" data-testid="avail-popup-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-zinc-500 mb-5">{t("pl.availability.contact_host")}</p>

              <div className="space-y-2.5">
                {phone && (
                  <a href={`tel:${phone}`}
                    className="flex items-center gap-3 bg-zinc-900 text-white rounded-2xl px-4 py-3 font-bold text-sm hover:bg-zinc-800 transition-colors"
                    data-testid="avail-popup-phone">
                    <Phone className="w-4 h-4" /> {t("pl.availability.call")} {phone}
                  </a>
                )}
                {whatsapp && (
                  <a href={`https://wa.me/${whatsapp.replace(/[^\d+]/g, "")}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-emerald-600 text-white rounded-2xl px-4 py-3 font-bold text-sm hover:bg-emerald-700 transition-colors"
                    data-testid="avail-popup-wa">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                )}
                {email && (
                  <button
                    type="button"
                    onClick={() => {
                      const subject = `Disponibilità ${popupDate} - ${property?.name || ""}`;
                      window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
                    }}
                    className="w-full flex items-center gap-3 bg-white border-2 border-zinc-200 text-zinc-900 rounded-2xl px-4 py-3 font-bold text-sm hover:border-zinc-400 transition-colors"
                    data-testid="avail-popup-mail">
                    <Mail className="w-4 h-4" /> {t("pl.availability.write_email")}
                  </button>
                )}

                {/* OTA quick-book links */}
                {Object.entries(otaLinks).filter(([, v]) => !!v).length > 0 && (
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 pt-3 pb-1">{t("pl.availability.book_on")}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(otaLinks).filter(([, v]) => !!v).map(([k, url]) => (
                        <a key={k} href={url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1.5 bg-zinc-100 text-zinc-900 rounded-xl px-3 py-2.5 font-bold text-xs hover:bg-zinc-200 transition-colors capitalize"
                          data-testid={`avail-popup-ota-${k}`}>
                          <Globe className="w-3.5 h-3.5" /> {k}
                        </a>
                      ))}
                    </div>
                  </>
                )}

                {!phone && !email && !whatsapp && Object.entries(otaLinks).filter(([, v]) => !!v).length === 0 && (
                  <p className="text-center text-sm text-zinc-400 italic py-4">{t("pl.availability.no_contacts")}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PublicAvailability;
